/**
 * Healthcare facility types and `feature_config` for the medonc dashboard.
 */

import { HealthCareFacility } from "risa-data-model";

/**
 * Feature flags and EV/BV validation toggles for a facility, stored under `feature_config`
 * on the Firestore healthcare_facility document.
 */
export interface HealthFacilityFeatureConfig {
  enable_review_mode: boolean;
  enable_oncoemr_tasks: boolean;
  validate_bv: boolean;
  validate_ev: boolean;
  /** When true, copay and co-insurance are required on EV coverage save/continue. */
  validate_copay_coinsurance: boolean;
  qc_notes_visible_auth_statuses?: string[];
  enable_candid_auth_letter_upload: boolean;
  /** When true, the header surfaces primary Dx validation status icons. */
  dx_validation_required: boolean;
  /**
   * Auth statuses (matched against the order's `auth_on_file.auth_status`) for
   * which primary Dx validation applies. An order only requires validation when
   * its determined auth status is included in this list.
   */
  dx_validation_auth_statuses: string[];
  /** When true, primary Dx changes require reason and source fields. */
  need_reason_for_dx_change: boolean;
  /** When true, site NPI is required for all orders. */
  is_site_npi_required: boolean;
}

/** Keys of {@link HealthFacilityFeatureConfig} for iteration and form field names. */
export type HealthFacilityFeatureConfigKey = keyof HealthFacilityFeatureConfig;

/**
 * One row of `review_pending_config`: maps a worklist `master_auth_status` to UI
 * label and allowed `bo_status` ids used when deciding to flip review state.
 */
export interface HealthFacilityReviewPendingConfigEntry {
  /** Must match the order's `master_auth_status` / Algolia facet (e.g. `no_auth_required`). */
  auth_status: string;
  /** Sidebar / tab label for this review-pending bucket. */
  label: string;
  /**
   * Expected `bo_status` option ids on save for this auth bucket when enabling agent
   * review pending; a match against any entry counts.
   */
  review_bo_status?: string[];
}

/**
 * Extended health-care facility model with dashboard-specific configuration fields
 * such as EMR integration, review mode settings, and organization mapping.
 */
export interface HealthCareFacilityModel extends HealthCareFacility {
  client_id?: string;
  is_external_organization?: boolean;
  organization_id?: string;
  emr_name?: string;
  fhir_client?: string;
  RisaFaxNumber?: string;
  RisaPhoneNumber?: string;
  updated_at?: string;
  /** Firestore allows `null`; avoid `undefined` on writes without `ignoreUndefinedProperties`. */
  updated_by?: string | null;
  analytics_url?: string;
  feature_config?: HealthFacilityFeatureConfig;
  onco_emr_base_url?: string;
  inactive_insurance_bo_status?: string;
  auth_letter_type?: string;
  auth_status_filter?: string[];
  corresponding_internal_org_id?: string;
  review_pending_config?: HealthFacilityReviewPendingConfigEntry[];
  /** GI data route selector — when set to "api", fetches from the API-based Firestore path. */
  global_route?: string;
  rpa_draft_submission_portal?: string[];
  enable_candid_auth_letter_upload?: boolean;
  category?: string;
  /** Service types offered by this facility (e.g. infusion, radiation). */
  service_types?: string[];
  reasons_min_count?: number;
  is_site_npi_required?: boolean;
  dx_source_policy?: Record<string, boolean>;
}

/** Default `feature_config` for empty forms and when merging partial API data. */
export const DEFAULT_HEALTH_FACILITY_FEATURE_CONFIG: HealthFacilityFeatureConfig =
  {
    enable_review_mode: false,
    enable_oncoemr_tasks: false,
    validate_bv: false,
    validate_ev: false,
    validate_copay_coinsurance: false,
    enable_candid_auth_letter_upload: false,
    dx_validation_required: false,
    dx_validation_auth_statuses: [],
    need_reason_for_dx_change: false,
    is_site_npi_required: false,
  };

/** Keys in display / persistence order (matches `HealthFacilityFeatureConfig` fields). */
export const HEALTH_FACILITY_FEATURE_CONFIG_KEYS: readonly HealthFacilityFeatureConfigKey[] =
  [
    "enable_review_mode",
    "enable_oncoemr_tasks",
    "validate_bv",
    "validate_ev",
    "validate_copay_coinsurance",
    "enable_candid_auth_letter_upload",
    "dx_validation_required",
    "dx_validation_auth_statuses",
    "need_reason_for_dx_change",
    "is_site_npi_required",
  ];

/**
 * Returns whether a feature-config flag is enabled for the facility.
 *
 * @param facility - Current facility model (from Redux or form state)
 * @param key - One of the known config keys
 * @returns True when the flag is enabled in `feature_config`
 */
export function isHealthFacilityFeatureEnabled(
  facility: HealthCareFacilityModel | undefined,
  key: HealthFacilityFeatureConfigKey,
): boolean {
  return facility?.feature_config?.[key] === true;
}

/** Sentinel value in {@link HealthFacilityFeatureConfig.qc_notes_visible_auth_statuses}. */
export const QC_NOTES_VISIBLE_FOR_ALL_AUTH_STATUSES = "ALL";

/**
 * Whether writeback QC notes (and the pre–Trigger RPA validation flow) should apply
 * for the order's current auth-on-file status and facility allowlist.
 *
 * @param featureConfig - Facility `feature_config`; if `qc_notes_visible_auth_statuses`
 *   is missing, null, or empty, QC is off for every status.
 * @param authOnFileAuthStatus - Current `auth_on_file.auth_status` on the order
 * @returns True when QC should be shown for this status
 */
export function shouldShowWritebackQcNotesForAuthStatus(
  featureConfig: HealthFacilityFeatureConfig | undefined,
  authOnFileAuthStatus: string | undefined,
): boolean {
  const allowlist = featureConfig?.qc_notes_visible_auth_statuses;
  if (allowlist === undefined || allowlist === null || allowlist.length === 0) {
    return false;
  }
  const upperAll = QC_NOTES_VISIBLE_FOR_ALL_AUTH_STATUSES.toUpperCase();
  if (
    allowlist.some((entry) => (entry ?? "").trim().toUpperCase() === upperAll)
  ) {
    return true;
  }
  const status = (authOnFileAuthStatus ?? "").trim();
  if (!status) {
    return false;
  }
  return allowlist.some((entry) => (entry ?? "").trim() === status);
}
/**
 * Minimal facility slice used to resolve per-`master_auth_status` review BO statuses.
 * Matches Firestore fields read by medonc; auth-wrapper types omit `review_pending_config`.
 */
export type FacilityReviewPendingConfigSource = {
  review_pending_config?: HealthFacilityReviewPendingConfigEntry[];
};

/**
 * Normalizes `review_bo_status` from facility config: canonical shape is `string[]`;
 * a legacy single string from Firestore is wrapped as a one-element array.
 *
 * @param value - Config value (array, legacy string, or absent)
 * @returns Non-empty list of BO ids, or `undefined` when unset / empty
 */
export function normalizeReviewBoStatusesFromFacilityValue(
  value: string | string[] | undefined | null,
): string[] | undefined {
  if (value == null) return undefined;
  if (Array.isArray(value)) {
    const filtered = value.filter(
      (s): s is string => typeof s === "string" && s.length > 0,
    );
    return filtered.length > 0 ? filtered : undefined;
  }
  if (typeof value === "string" && value.length > 0) return [value];
  return undefined;
}

/**
 * Returns the facility-configured review BO status ids for a given order
 * `master_auth_status`, using the matching `review_pending_config` row.
 *
 * @param facility - Loaded healthcare facility (may omit `review_pending_config`)
 * @param masterAuthStatus - Current order `master_auth_status`
 * @returns `review_bo_status` list for that row, or `undefined` when none match
 */
export function getReviewBoStatusesForMasterAuthStatus(
  facility: FacilityReviewPendingConfigSource | undefined | null,
  masterAuthStatus: string | null | undefined,
): string[] | undefined {
  if (!masterAuthStatus?.trim()) return undefined;
  const rows = facility?.review_pending_config;
  if (!rows?.length) return undefined;
  const raw = rows.find((row) => row.auth_status === masterAuthStatus)
    ?.review_bo_status as string | string[] | undefined;
  return normalizeReviewBoStatusesFromFacilityValue(raw);
}
